// src/components/preview/leave/LeaveRequestPreview.jsx
// ✅ 휴가신청서(연차) 미리보기·인쇄(2026-10-19) — 결의서 미리보기(ResolutionPreview)와 같은 틀:
// 제목 + 우상단 결재란(도장 칸) + 필드-값 2열 표, A4 용지, html2pdf로 PDF 미리보기·다운로드.
// 값은 전부 서버가 신청 시점에 저장한 template_data(스냅샷)를 그대로 읽는다(화면에서 다시 계산하지 않는다).
import React, { useRef } from "react";
import html2pdf from "html2pdf.js";
import { drafterTeamOf } from "../../../utils/resolutionDraft";
import HoursText from "./HoursText";
import "../income/IncomeProposalPreview.css";
import "../resolution/ResolutionPreview.css";
import "./LeaveRequestPreview.css";

// 결재란 원형 표시 — 기안 칸은 "기안", 전결은 "전결", 결재는 "승인"
const BADGE = { 기안: "기안", 전결: "전결", 결재: "승인", 합의: "합의" };

const dot = (v) => (v ? String(v).slice(0, 10).replace(/-/g, ".") : "");

// 저장된 문서(상세 API) 또는 저장 전 미리보기 값 → 화면 값
export function buildLeaveRequestProps(doc) {
  const td = doc.template_data || doc;
  const applicant = td.applicant || {};
  const lines = (doc.approval_lines || doc.all_approval_lines || []).map((l) => ({
    id: l.id,
    position: l.position || "",
    name: l.approver_name || l.approver__name || l.name || "",
    approvalType: l.approval_type || "",
    result: l.result,
    approvedAt: l.approved_at,
    comment: l.comment || "",
  }));
  return {
    draftNo: doc.draft_no || td.draft_no_preview || "",
    draftDate: doc.draft_date || "",
    applicant,
    team: drafterTeamOf(applicant.name),
    td,
    lines,
    references: doc.references
      ? doc.references.map((r) => r.user_name).filter(Boolean)
      : [...(doc.chosen_cc || []), ...(td.auto_cc || []).map((n) => `${n}(자동)`)],
    stateLabel: doc.leave_state_label || "",
    isDraft: !doc.id,
  };
}

export default function LeaveRequestPreview({ draftNo, draftDate, applicant = {}, team, td = {}, lines = [],
  references = [], stateLabel, isDraft }) {
  const paperRef = useRef(null);
  const snap = td.snapshot || {};
  const texts = snap.texts || {};
  const decision = [...lines].reverse().find((l) => l.approvalType !== "기안");

  const withPaper = (run) => {
    const el = paperRef.current;
    if (!el) return;
    const original = el.style.height;
    el.style.height = "297mm";
    const opt = {
      margin: 0,
      filename: `휴가신청서_${applicant.name || ""}_${td.start_date || ""}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false, backgroundColor: "#ffffff", width: 794, height: 1123 },
      jsPDF: { unit: "px", format: [794, 1123], orientation: "portrait", hotfixes: ["px_scaling"] },
    };
    run(html2pdf().set(opt).from(el))
      .then(() => (el.style.height = original))
      .catch(() => (el.style.height = original));
  };
  const handlePreviewPDF = () =>
    withPaper((chain) =>
      chain.outputPdf("blob").then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 100);
      })
    );
  const handleDownloadPDF = () => withPaper((chain) => chain.save());

  // 저장 전에는 결재란이 비어 있다 — 기안자·결재자 자리만 보여준다
  const boxes = lines.length ? lines : [
    { id: "d", position: applicant.position, name: applicant.name, approvalType: "기안" },
    td.approver?.is_self
      ? { id: "a", position: "", name: "전결", approvalType: "전결" }
      : { id: "a", position: td.approver?.position || "", name: td.approver?.name || "결재", approvalType: "결재" },
  ];

  return (
    <div className="income-preview-wrapper">
      <div className="preview-header">
        <h3 className="preview-title">📄 미리보기</h3>
        {!isDraft && (
          <div style={{ display: "flex", gap: "10px" }}>
            <button type="button" onClick={handlePreviewPDF} className="print-button preview-button">
              👁️ PDF 미리보기
            </button>
            <button type="button" onClick={handleDownloadPDF} className="print-button download-button">
              📥 PDF 다운로드
            </button>
          </div>
        )}
      </div>

      <div className="income-preview-container">
        <div className="preview-paper" ref={paperRef}>
          <div className="er-title">휴가신청서(연차)</div>

          <div className="er-approval-wrap">
            <div className="er-approval-grid">
              {boxes.map((a, i) => (
                <div className="er-approval-box" key={a.id ?? i}>
                  <div className="er-approval-header">{[a.position, a.name].filter(Boolean).join(" ") || "-"}</div>
                  <div className="er-approval-body">
                    {a.result === "approved" && (
                      <>
                        <div className="er-approval-badge">{BADGE[a.approvalType] || "승인"}</div>
                        <div className="er-approval-date">{dot(a.approvedAt)}</div>
                      </>
                    )}
                    {a.result === "rejected" && (
                      <>
                        <div className="lr-rejected">반려</div>
                        <div className="er-approval-date">{dot(a.approvedAt)}</div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <table className="er-field-table">
            <tbody>
              <tr><th>문서번호</th><td>{draftNo || "-"}{isDraft && draftNo ? " (예정 — 신청 시 확정)" : ""}</td></tr>
              <tr>
                <th>기안자</th>
                <td>{[team, [applicant.position, applicant.name].filter(Boolean).join(" ")].filter(Boolean).join(" / ") || "-"}</td>
              </tr>
              <tr><th>작성일자</th><td>{draftDate || td.draft_date || "-"}</td></tr>
              <tr><th>휴가구분</th><td>{td.kind_label || "-"}</td></tr>
              <tr><th>휴가기간</th><td>{td.period_text || "-"}</td></tr>
              <tr>
                <th>소요시간</th>
                <td>
                  {td.working_days
                    ? `근무일 ${td.working_days.length}일 × 하루 ${td.per_day_hours}시간 (주말·공휴일 제외)`
                    : "-"}
                </td>
              </tr>
              <tr><th>휴가차감</th><td><HoursText text={td.hours_text} strong align="start" /></td></tr>
              <tr><th>휴가사유</th><td>{td.reason || "-"}</td></tr>
              <tr>
                <th>휴가 신청정보</th>
                <td className="lr-snap-cell">
                  {/* "총 부여"·"사용"은 뺐다(2026-10-22) — 부여 기록이 없어 잔여 + 사용을 역산한 추정치라 공식
                      문서에 부여일수로 읽히면 안 된다. 저장된 스냅샷(granted·used)은 그대로 두고 그리지만 않는다.
                      부여 테이블이 생기면 실측값으로 두 열을 되살린다(CLAUDE.md 백로그 "연차 부여 테이블"). */}
                  <table className="lr-snap">
                    {/* 바깥 칸 폭에 꽉 차게 고정 비율(2026-10-22) — 두 줄 표기가 줄바꿈 없이 들어간다 */}
                    <colgroup>
                      <col style={{ width: "20%" }} />
                      <col style={{ width: "26%" }} />
                      <col style={{ width: "26%" }} />
                      <col style={{ width: "28%" }} />
                    </colgroup>
                    <thead>
                      <tr><th>귀속년도</th><th>잔여</th><th>이번 차감</th><th>차감 후 잔여</th></tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{snap.year ? `${snap.year}년` : "-"}</td>
                        <td><HoursText text={texts.remaining} /></td>
                        <td><HoursText text={texts.this} /></td>
                        <td><HoursText text={texts.after} /></td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="lr-note">신청 시점 값(고정) · 연차·반차 공용 잔여</div>
                </td>
              </tr>
              <tr><th>수신참조</th><td>{references.length ? references.join(", ") : "-"}</td></tr>
              {!isDraft && (
                <tr>
                  <th>결재 결과</th>
                  <td>
                    {stateLabel || "-"}
                    {decision?.approvalType === "전결" && decision.comment ? ` — ${decision.comment}` : ""}
                    {decision?.result === "rejected" && decision.comment ? ` — 반려 사유: ${decision.comment}` : ""}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
